import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from '@24i/nxg-core-router/src/NextRouter';
import { BOTTOM_TAB_BAR_PADDING } from '@24i/nxg-core-utils/src/constants';
import { useTheme } from '@24i/nxg-sdk-higgs';
import { View } from '@24i/nxg-sdk-quarks';
import { overridable } from '@24i/nxg-sdk-gluons/src/context/ComponentOverrides';
import { NavScreenConfigWeb } from '@24i/nxg-sdk-gluons/src/clients/AppNavigationClient';
import {
    isWebView,
    getTelegramWebAppBottomSafeAreaInset,
    isAndroidWeb,
} from '@24i/nxg-sdk-higgs/src/utils';
import type { Theme } from '@24i/nxg-sdk-photon';

import { ViewStyle } from 'react-native';
import { getBottomTabBarStyles } from './styles/index.web';
import { TopBarMenuItem } from '../../../components/MenuItem';
import { useMenuItemPress } from '../../utils';

interface BottomTabBarProps {
    styles?: (theme: Theme) => {
        menuContainer?: ViewStyle;
        section?: ViewStyle;
        sectionContainer?: ViewStyle;
    };
    menuItems?: NavScreenConfigWeb[];
}

const BottomTabBar: React.FC<BottomTabBarProps> = ({
    styles: getStyles = getBottomTabBarStyles,
    menuItems = [],
}) => {
    const router = useRouter();
    const { theme } = useTheme();
    const { t } = useTranslation(['sott']);
    const onPress = useMenuItemPress();
    const bottomInset = getTelegramWebAppBottomSafeAreaInset?.() || 0;

    const styles = getStyles(theme);

    const getMenuItemComponent = (menuItem: NavScreenConfigWeb, index: number) => {
        const { icon, menuTitle, name, optionsWeb: options, webMenuLink } = menuItem;

        let titleLabel;
        if (options?.showTitle !== false) {
            const lowerCaseName = name.toLowerCase();
            if (lowerCaseName === 'searching' || lowerCaseName === 'search') {
                titleLabel = t(`search.title`);
            } else {
                titleLabel = t(`menu.${menuTitle}`);
            }
        } else {
            titleLabel = '';
        }

        // Check if this is the home item and we're at root path
        const isHome =
            name.toLowerCase() === 'home' && (router.asPath === '/' || router.asPath === '');

        // Check if current path matches this menu item's path or links
        const isPathMatch = router.asPath.includes(name);
        const isWebLinkMatch = webMenuLink?.as && router.asPath.includes(webMenuLink.as.toString());

        // Item is active if any condition is true
        const isActive = isPathMatch || isWebLinkMatch || isHome;

        return (
            <View key={`bottommenu-link-${name}`}>
                <TopBarMenuItem
                    key={`bottommenu-${menuTitle}`}
                    title={titleLabel}
                    icon={icon}
                    index={index}
                    active={isActive}
                    spatial
                    onPress={() => onPress(menuItem)}
                    variant="bottom-menu"
                />
            </View>
        );
    };

    const paddingBottom: string | number = useMemo(() => {
        let newPaddingBottom: string | number = isAndroidWeb() ? 48 : 0;

        // More thorough check for CSS availability
        if (
            typeof CSS !== 'undefined' &&
            CSS &&
            typeof CSS.supports === 'function' &&
            CSS.supports('padding-bottom', 'env(safe-area-inset-bottom)') &&
            CSS.supports('padding-bottom', 'var(--custom-safe-area-bottom)')
        ) {
            if (isWebView()) {
                // To force a different value (e.g., for WebView)
                document.documentElement.style.setProperty(
                    '--custom-safe-area-bottom',
                    `${bottomInset || newPaddingBottom}px`
                );
            }
            newPaddingBottom = `var(--custom-safe-area-bottom, ${newPaddingBottom}px)`;
        }
        return newPaddingBottom;
    }, [bottomInset]);

    return (
        <View
            style={{
                ...styles.menuContainer,
                paddingBottom,
                marginTop: 200,
            }}
            className="bottom-menu-tabbar"
        >
            <style global jsx>{`
                div#__next {
                    padding-bottom: ${BOTTOM_TAB_BAR_PADDING}px;
                }
            `}</style>
            <View style={styles.sectionContainer}>
                <View
                    style={{
                        ...styles.section,
                    }}
                >
                    {menuItems.map(getMenuItemComponent)}
                </View>
            </View>
        </View>
    );
};

export default overridable(BottomTabBar, 'BottomTabBar');
